const activeFlags = (task) => task?.status !== 'removed' && !task?.flagsDismissed && !task?.flagsKept
  && Boolean(task?.hasFlags || (task?.flags || task?.flagCounts)?.predator > 0 || (task?.flags || task?.flagCounts)?.interesting > 0);

function inView(task, filter) {
  if (task.status === 'removed' || task.archived) return false;
  if (filter === 'flagged') return activeFlags(task);
  if (filter === 'autoFlagged') return Boolean(task.autoFlagged);
  if (filter === 'closed') return task.status === 'closed';
  if (filter === 'open') return task.status === 'open';
  if (filter === 'needsReview') return task.status === 'open' && task.needsReview !== false;
  return true;
}

const patchFor = ({ kind, payload }) => ({
  ...(kind === 'resolve' ? { status: payload.outcome === 'removed' ? 'removed' : 'closed',
    outcome: payload.outcome === 'removed' ? null : payload.outcome, speciesId: payload.speciesId,
    needsReview: false, resolutionSource: 'admin', autoFlagged: false } : {}),
  ...(payload.keepFlags ? { hasFlags: false, flagsKept: true } : {}),
  ...(payload.outcome === 'removed' ? { hasFlags: false, flagsKept: false } : {}),
  ...(kind === 'dismiss' || payload.dismissFlags ? { hasFlags: false, flagsDismissed: true, flagsKept: false } : {}),
});

// Shared by the grid, rather than by cards that disappear as soon as they are handled.
// Calls for the same track are ordered; different tracks use up to four requests.
export function createMotionAdminSaveQueue(api, concurrency = 4) {
  const records = new Map();
  const listeners = new Set();
  let running = 0;
  let version = 0;
  let sequence = 0;
  let notice = '';
  const emit = () => { version += 1; listeners.forEach((fn) => fn()); };
  const effective = (record, base = record.base) => record.pending.reduce((task, operation) => ({ ...task, ...patchFor(operation) }), { ...base, ...record.confirmed });

  const pump = () => {
    for (const record of records.values()) {
      if (running >= concurrency) break;
      if (record.running || !record.pending.length) continue;
      const operation = record.pending[0];
      record.running = true; running += 1;
      let request;
      try { request = operation.kind === 'resolve' ? api.resolve(operation.payload) : api.dismissFlags(operation.payload); }
      catch (error) { request = Promise.reject(error); }
      Promise.resolve(request).then(() => {
        Object.assign(record.confirmed, patchFor(operation));
        record.failed = record.failed.filter((item) => item.kind !== operation.kind || item.sequence > operation.sequence);
        notice = operation.kind === 'dismiss' ? 'Flags dismissed. The track and its motion decision have been kept.'
          : operation.payload.outcome === 'removed' ? 'Track removed from review and excluded from training.'
            : operation.payload.keepFlags ? 'Marked as real motion and kept in Highlights. Removed from the Flagged queue.'
              : operation.payload.dismissFlags ? 'Staff motion decision saved and flags dismissed.' : 'Staff motion decision saved.';
      }, (error) => {
        // Later queued choices remain visible, but this failed choice is rolled back.
        record.failed = record.failed.filter((item) => item.kind !== operation.kind);
        record.failed.push({ ...operation, error });
      }).finally(() => {
        record.pending.shift(); record.running = false; running -= 1;
        if (!record.pending.length && !record.failed.length) {
          const { id, status, flags, flagCounts, hasFlags, flagsDismissed, flagsKept, archived } = record.base;
          record.base = { id, status, flags, flagCounts, hasFlags, flagsDismissed, flagsKept, archived };
        }
        emit(); pump();
      });
    }
  };

  function enqueue(kind, payload, task, retryOperation) {
    let record = records.get(payload.taskId);
    if (!record) {
      record = { base: task, confirmed: {}, pending: [], failed: [], running: false, statsFlagged: activeFlags(task) };
      records.set(payload.taskId, record);
    }
    if (task && !record.pending.length && !record.failed.length) record.base = task;
    const signature = JSON.stringify({ kind, ...payload, requestId: undefined });
    const last = record.pending.filter((item) => item.kind === kind).at(-1);
    if (last?.signature === signature) return;
    record.failed = record.failed.filter((item) => item.kind !== kind);
    const operation = retryOperation || { kind, signature, sequence: ++sequence, payload: { ...payload,
      ...(kind === 'resolve' ? { requestId: window.crypto?.randomUUID?.() || `staff-${Date.now()}-${Math.random().toString(36).slice(2)}` } : {}) } };
    record.pending.push(operation); notice = ''; emit(); pump();
  }

  return {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    enqueue,
    retry(operation) { enqueue(operation.kind, operation.payload, records.get(operation.payload.taskId)?.base, operation); },
    get version() { return version; },
    get notice() { return notice; },
    get pendingCount() { return [...records.values()].reduce((sum, item) => sum + item.pending.length, 0); },
    get failures() { return [...records.values()].flatMap((record) => record.failed); },
    get flaggedDelta() { return [...records.values()].reduce((sum, record) => sum + Number(activeFlags(effective(record))) - Number(record.statsFlagged), 0); },
    // Totals may arrive before cards. Rebase the displayed flag count without
    // dropping the saved card overlays while older cards are still visible.
    acceptStats(startedVersion) {
      if (startedVersion !== version || this.pendingCount) return false;
      for (const record of records.values()) record.statsFlagged = activeFlags(effective(record));
      return true;
    },
    // Accept only a fresh full refresh started after all writes finished.
    acceptRefresh(startedVersion) {
      if (startedVersion !== version || this.pendingCount) return false;
      for (const [id, record] of records) {
        if (!record.failed.length) records.delete(id);
        else record.statsFlagged = activeFlags(effective(record));
      }
      return true;
    },
    project(tasks, filter) {
      const present = new Set(tasks.map((task) => task.id));
      const result = tasks.flatMap((task) => {
        const record = records.get(task.id);
        if (!record) return [task];
        const updated = effective(record, task);
        // A failed request with no effective change restores the original server row.
        if (!record.pending.length && !Object.keys(record.confirmed).length) return [task];
        return inView(updated, filter) ? [updated] : [];
      });
      for (const [id, record] of records) {
        if (present.has(id) || (!record.pending.length && !record.failed.length)) continue;
        const updated = effective(record);
        if (inView(updated, filter)) result.push(updated);
      }
      return result;
    },
  };
}
